import React, { useState } from 'react';
import { UserPlus, Loader2, ShieldCheck, Clock, XCircle, Trash2 } from 'lucide-react';
import { api } from '../../context/AuthContext';

const STATUS = {
  aktiv: { label: 'Freigegeben', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: ShieldCheck },
  angefragt: { label: 'Wartet auf Freigabe des Mandanten', cls: 'bg-amber-50 text-amber-700 border-amber-200', Icon: Clock },
  abgelehnt: { label: 'Vom Mandanten abgelehnt', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: XCircle },
  widerrufen: { label: 'Widerrufen', cls: 'bg-gray-50 text-gray-500 border-gray-200', Icon: XCircle },
};

const akteLabel = (akten, list) => (akten === 'alle' ? 'Alle Akten' : (akten || []).map((id) => list.find((a) => a.case_id === id)?.aktenzeichen || list.find((a) => a.case_id === id)?.rechtsgebiet || id).join(', '));

const NeuForm = ({ mnr, akten, onDone }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [scope, setScope] = useState('alle');
  const [sel, setSel] = useState([]);
  const [einladen, setEinladen] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const submit = async () => {
    setBusy(true); setMsg('');
    try {
      await api.post(`/register/mandanten/${mnr}/nutzer`, { email, name, akten: scope === 'alle' ? 'alle' : sel, einladen });
      setEmail(''); setName(''); setSel([]); setMsg('Anfrage gesendet – der Mandant muss die Freigabe in seinem Portal bestätigen.'); onDone();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Fehler'); }
    setBusy(false);
  };

  return (
    <div className="p-3 bg-[#fbf7f0] border-l-2 border-[#b8893d] space-y-2" data-testid="nutzer-neu-form">
      <div className="grid sm:grid-cols-2 gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name (z. B. Buchhaltung)" data-testid="nutzer-neu-name" className="px-2.5 py-1.5 text-sm border border-gray-200 bg-white focus:outline-none focus:border-[#b8893d]" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-Mail" type="email" data-testid="nutzer-neu-email" className="px-2.5 py-1.5 text-sm border border-gray-200 bg-white focus:outline-none focus:border-[#b8893d]" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#1B3A5C]">
        <label className="inline-flex items-center gap-1.5"><input type="radio" checked={scope === 'alle'} onChange={() => setScope('alle')} data-testid="nutzer-scope-alle" /> Alle Akten</label>
        <label className="inline-flex items-center gap-1.5"><input type="radio" checked={scope === 'auswahl'} onChange={() => setScope('auswahl')} data-testid="nutzer-scope-auswahl" /> Nur ausgewählte Akten</label>
        <label className="inline-flex items-center gap-1.5 ml-auto"><input type="checkbox" checked={einladen} onChange={(e) => setEinladen(e.target.checked)} data-testid="nutzer-neu-einladen" /> Website-Zugang einrichten, falls nicht vorhanden</label>
      </div>
      {scope === 'auswahl' && (
        <div className="flex flex-wrap gap-1.5">
          {akten.filter((a) => !a.archived).map((a) => (
            <button key={a.case_id} type="button" onClick={() => toggle(a.case_id)} data-testid={`nutzer-akte-${a.case_id}`}
              className={`px-2.5 py-1 text-[11px] rounded-full border ${sel.includes(a.case_id) ? 'bg-[#1B3A5C] border-[#1B3A5C] text-white' : 'border-gray-200 bg-white text-[#1B3A5C]'}`}>
              {a.aktenzeichen ? `AZ ${a.aktenzeichen}` : a.rechtsgebiet}
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] text-[#34536b]" data-testid="nutzer-neu-msg">{msg}</span>
        <button type="button" onClick={submit} disabled={busy || !email.includes('@') || (scope === 'auswahl' && !sel.length)} data-testid="nutzer-neu-submit"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] tracking-wider bg-[#1B3A5C] text-white disabled:opacity-40">{busy && <Loader2 className="w-3 h-3 animate-spin" />} FREIGABE ANFRAGEN</button>
      </div>
    </div>
  );
};

export const NutzerPanel = ({ mnr, konten, nutzer, akten, onChanged }) => {
  const [neu, setNeu] = useState(false);
  const entziehen = async (n) => {
    if (!window.confirm(`Zugriff für ${n.email} entziehen?`)) return;
    await api.delete(`/register/nutzer/${n.id}`); onChanged();
  };
  return (
    <div className="space-y-2" data-testid="nutzer-panel">
      {konten.map((k) => (
        <div key={k.email} className="flex items-center gap-2 text-sm px-3 py-2 border border-gray-100 bg-white" data-testid="konto-haupt">
          <span className="text-[10px] tracking-wider px-2 py-0.5 bg-[#1B3A5C] text-white rounded-full">HAUPTZUGANG</span>
          <span className="text-[#1B3A5C] font-medium">{k.name || k.email}</span><span className="text-[#7a8a99] text-xs">{k.email}</span>
          {!k.passwort_gesetzt && <span className="ml-auto text-[10px] text-amber-700">Passwort noch nicht gesetzt</span>}
        </div>
      ))}
      {konten.length === 0 && <p className="text-xs text-[#7a8a99]">Kein Website-Hauptzugang für die Mandanten-E-Mail vorhanden.</p>}
      {nutzer.map((n) => {
        const st = STATUS[n.status] || STATUS.angefragt;
        return (
          <div key={n.id} className="flex flex-wrap items-center gap-2 text-sm px-3 py-2 border border-gray-100 bg-white" data-testid={`nutzer-${n.id}`}>
            <span className="text-[10px] tracking-wider px-2 py-0.5 border border-gray-200 text-[#34536b] rounded-full">ZUSATZ</span>
            <span className="text-[#1B3A5C] font-medium">{n.name || n.email}</span><span className="text-[#7a8a99] text-xs">{n.email}</span>
            <span className="text-[11px] text-[#34536b]">· {akteLabel(n.akten, akten)}</span>
            <span className={`ml-auto inline-flex items-center gap-1 text-[10px] px-2 py-0.5 border rounded-full ${st.cls}`} data-testid={`nutzer-status-${n.id}`}><st.Icon className="w-3 h-3" /> {st.label}</span>
            {['aktiv', 'angefragt'].includes(n.status) && <button type="button" onClick={() => entziehen(n)} title="Zugriff entziehen" data-testid={`nutzer-entziehen-${n.id}`} className="p-1 text-[#7a8a99] hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        );
      })}
      <button type="button" onClick={() => setNeu((v) => !v)} data-testid="nutzer-neu-toggle" className="inline-flex items-center gap-1.5 text-[11px] tracking-wider text-[#b8893d] hover:text-[#1B3A5C]"><UserPlus className="w-3.5 h-3.5" /> WEITEREN NUTZER ZUORDNEN</button>
      {neu && <NeuForm mnr={mnr} akten={akten} onDone={onChanged} />}
    </div>
  );
};

export default NutzerPanel;
